/**
 * Which payment methods a unit can offer, from the platform settings document
 * (systemSettings/platform) and the unit document. Shared by the invoice PaymentPage
 * and the payment link form. Cloud Functions apply the same rules
 * (backend/functions/src/paymentLinks/availableMethods.ts).
 */

import { isSlickPayReadyForPayments, type PaymentMethodType, type UnitPaymentMethods } from '@masonic-tools/shared-types';

export interface PlatformMethodSetting {
  enabled: boolean;
  /** GBP */
  transactionFee: number;
}

export interface PlatformPaymentSettings {
  bankTransfer: PlatformMethodSetting;
  stripe: PlatformMethodSetting;
  sumup: PlatformMethodSetting;
  slickpay: PlatformMethodSetting;
}

export const DEFAULT_PLATFORM_PAYMENT_SETTINGS: PlatformPaymentSettings = {
  bankTransfer: { enabled: true, transactionFee: 0 },
  stripe: { enabled: true, transactionFee: 0 },
  sumup: { enabled: false, transactionFee: 0 },
  slickpay: { enabled: false, transactionFee: 0 },
};

function num(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : fallback;
}

/** Parse `systemSettings/platform`, using the same fallbacks as PaymentPage. */
export function parsePlatformPaymentSettings(data: Record<string, any> | undefined | null): PlatformPaymentSettings {
  if (!data) return { ...DEFAULT_PLATFORM_PAYMENT_SETTINGS };
  const pm = data.paymentMethods || {};
  return {
    bankTransfer: {
      enabled: pm.bankTransfer?.enabled ?? true,
      transactionFee: num(pm.bankTransfer?.transactionFee, 0),
    },
    stripe: {
      enabled: pm.stripe?.enabled ?? (data.processPaymentViaCard ?? true),
      transactionFee: num(pm.stripe?.transactionFee, num(data.transactionFee, 0)),
    },
    sumup: { enabled: pm.sumup?.enabled ?? false, transactionFee: num(pm.sumup?.transactionFee, 0) },
    slickpay: { enabled: pm.slickpay?.enabled ?? false, transactionFee: num(pm.slickpay?.transactionFee, 0) },
  };
}

export interface UnitPaymentData {
  stripeConnectedAccountId?: string | null;
  settings?: { paymentMethods?: Record<string, any> } | null;
}

/** Online methods (no bank transfer) the platform has switched on and the unit has set up. */
export function resolveOnlinePaymentMethods(
  unit: UnitPaymentData | null | undefined,
  platform: PlatformPaymentSettings,
  options: { requireSumUpKey?: boolean } = {},
): Array<Exclude<PaymentMethodType, 'bank_transfer'>> {
  const pm = (unit?.settings?.paymentMethods || {}) as Record<string, any>;
  const out: Array<Exclude<PaymentMethodType, 'bank_transfer'>> = [];
  if (platform.stripe.enabled && pm.stripe?.enabled && !!unit?.stripeConnectedAccountId) out.push('stripe');
  if (platform.sumup.enabled && pm.sumup?.enabled && (!options.requireSumUpKey || !!pm.sumup?.apiKeyEncrypted)) out.push('sumup');
  // Hidden while Hope Macy is still verifying the unit's bank account.
  if (platform.slickpay.enabled && isSlickPayReadyForPayments(pm as UnitPaymentMethods)) out.push('slickpay');
  return out;
}

/** Every method a payer can use on an invoice, including bank transfer. */
export function resolveInvoicePaymentMethods(
  unit: UnitPaymentData | null | undefined,
  platform: PlatformPaymentSettings,
): PaymentMethodType[] {
  const pm = (unit?.settings?.paymentMethods || {}) as Record<string, any>;
  const methods: PaymentMethodType[] = [];
  if (platform.bankTransfer.enabled && pm.bankTransfer?.enabled) methods.push('bank_transfer');
  methods.push(...resolveOnlinePaymentMethods(unit, platform));
  return Array.from(new Set(methods));
}
